import React, { useState } from "react";
import "../styles/Pricing.css";
import usePageMeta from "../hooks/usePageMeta";
import { PLANS, formatMoney, registerUrl, PlanId } from "../data/plans";

const PayPalSecureBadge = () => (
  <div className="paypal-secure-badge">
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
      <path d="M7 11V7a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v4"></path>
    </svg>
    <span>Secured by PayPal</span>
  </div>
);

interface PricingPageProps {
  redirectPath?: string;
}

const PricingPage: React.FC<PricingPageProps> = ({
  redirectPath = "/registration",
}) => {
  const [seatCount, setSeatCount] = useState(2);

  usePageMeta({
    title: "Pricing | PabSync",
    description:
      "Simple, transparent pricing. Start free, upgrade to Starter for unlimited projects, or choose per-user Team pricing.",
    path: "/pricing",
  });

  const handleSelect = (planId: PlanId) => {
    // Redirect to the web-app registration; pass seats for Team so it prefills.
    window.location.href = registerUrl(planId, {
      seats: planId === "team" ? seatCount : undefined,
    });
  };

  return (
    <div className="pricing-container">
      <div className="pricing-content">
        {/* Header */}
        <div className="pricing-header">
          <span className="pricing-eyebrow">Pricing</span>
          <h1 className="pricing-title">
            Choose the plan that fits your workflow
          </h1>
          <p className="pricing-subtitle">
            Starter is the best choice for solo power users. Team is clear
            per-user pricing for collaboration.
          </p>
          <div className="pricing-badge-container">
            <PayPalSecureBadge />
          </div>
        </div>

        {/* Plans Grid */}
        <div className="pricing-grid">
          {PLANS.map((plan) => {
            const isTeam = plan.id === "team";
            const seats = Math.max(seatCount, plan.minimumSeats || 1);
            const perUser = plan.pricePerUser || 0;
            const totalTeam = isTeam ? seats * perUser : plan.priceMonthly;

            const allItems = [
              ...(plan.highlights || []),
              ...(plan.limits || []),
            ];

            return (
              <div
                key={plan.id}
                className={`pricing-card ${
                  plan.isPopular ? "pricing-card--popular" : ""
                }`}
              >
                {/* Gradient overlay for popular card */}
                {plan.isPopular && <div className="pricing-card__gradient" />}

                {/* Badge */}
                {plan.badge && (
                  <div className="pricing-card__badge">
                    <span className="badge-icon">★</span>
                    <span>{plan.badge}</span>
                  </div>
                )}

                {/* Card content */}
                <div className="pricing-card__content">
                  {/* Plan name and description */}
                  <h3 className="pricing-card__name">{plan.name}</h3>
                  <p className="pricing-card__recommended">
                    {plan.recommendedFor}
                  </p>

                  {/* Pricing */}
                  <div className="pricing-card__pricing">
                    <div className="pricing-amount">
                      {plan.id === "team" && plan.pricePerUser
                        ? `${formatMoney(plan.pricePerUser)} / user / month`
                        : plan.priceMonthly === 0
                        ? "Free"
                        : `${formatMoney(plan.priceMonthly)} / month`}
                    </div>
                    <p className="pricing-card__caption">
                      {plan.id === "starter" &&
                        "Unlimited projects — no per-user complexity."}
                      {plan.id === "free" && "Zero friction onboarding."}
                      {plan.id === "team" && plan.pricePerUser && (
                        <>
                          Minimum {plan.minimumSeats || 2} seats • 2 users →{" "}
                          {formatMoney(2 * plan.pricePerUser)} / month • 5 users
                          → {formatMoney(5 * plan.pricePerUser)}
                        </>
                      )}
                    </p>
                  </div>

                  {/* Seats input for Team plan */}
                  {isTeam && (
                    <div className="pricing-card__seats">
                      <label htmlFor={`seats-${plan.id}`}>Seats</label>
                      <div className="seats-input-wrapper">
                        <svg
                          className="seats-icon"
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                          <circle cx="9" cy="7" r="4"></circle>
                          <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                          <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                        </svg>
                        <input
                          id={`seats-${plan.id}`}
                          type="number"
                          min={plan.minimumSeats || 1}
                          max={5000}
                          value={seatCount}
                          onChange={(e) => {
                            const min = plan.minimumSeats || 2;
                            const max = 5000;
                            const value =
                              parseInt(e.target.value || "0", 10) || 0;
                            setSeatCount(Math.min(Math.max(value, min), max));
                          }}
                          className="seats-input"
                        />
                      </div>
                      <small className="seats-helper">
                        Billed {formatMoney(totalTeam)} / month
                      </small>
                    </div>
                  )}

                  {/* CTA Button */}
                  <button
                    className={`pricing-card__button ${
                      plan.isPopular ? "pricing-card__button--primary" : ""
                    }`}
                    onClick={() => handleSelect(plan.id)}
                  >
                    {plan.ctaLabel || "Choose plan"}
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <polyline points="9 18 15 12 9 6"></polyline>
                    </svg>
                  </button>

                  {/* Divider */}
                  <hr className="pricing-card__divider" />

                  {/* Features list */}
                  <ul className="pricing-card__features">
                    {allItems.filter(Boolean).map((item, idx) => (
                      <li key={idx} className="pricing-card__feature">
                        <svg
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          className="feature-icon"
                        >
                          <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer note */}
        <div className="pricing-footer">
          <p>Billing changes are restricted to account owners / org admins.</p>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;
